import { create } from 'zustand';
import {
  createCookie,
  deleteCookie,
  getCookieValue,
  getListCookie,
} from '#/utils/cookies';
import { uuid } from '#/utils/functional/string/uuid';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';

export const FLASHES_COOKIE = 'flashMessages';

export type FlashStyle = 'success' | 'info' | 'warn' | 'error';

export type Flash = {
  message: string;
  style: FlashStyle;
};

export type FlashCookie = {
  type: 'flash';
  data: Flash;
};

export type FlashStoreItem = {
  id: string;
  keepOnceForNavigation: boolean;
  data: Flash;
};

function generateFlashId() {
  return uuid() + new Date().getTime().toString(36);
}

function getFlashesFromCookies(): FlashStoreItem[] {
  if (HORIZON_ENABLED) return [];

  // Only run in the client
  if (typeof window === 'undefined') return [];

  const cookieItems = getListCookie<FlashCookie>(FLASHES_COOKIE);
  deleteCookie(FLASHES_COOKIE);

  return cookieItems
    .filter((item) => {
      // Temporary check after migrating from flash message with type to string
      // Remove after April 15th/2026
      return item.data?.message && item.data.style;
    })
    .map(
      (item) =>
        ({
          id: generateFlashId(),
          data: item.data,
          keepOnceForNavigation: false,
        }) satisfies FlashStoreItem,
    );
}

const flashesStore = create<{
  flashes: FlashStoreItem[];
}>(() => ({ flashes: getFlashesFromCookies() }));

const useFlashesStore = flashesStore; // The store can be used as a hook

// Actions

function internalPushFlash(item: FlashStoreItem) {
  flashesStore.setState((state) => ({
    flashes: [...state.flashes, item],
  }));
}

export function flash(message: string, style?: FlashStyle) {
  internalPushFlash({
    id: generateFlashId(),
    keepOnceForNavigation: false,
    data: { message, style: style ?? 'success' },
  });
}

export function navigationFlash(message: string, style?: FlashStyle) {
  internalPushFlash({
    id: generateFlashId(),
    keepOnceForNavigation: true,
    data: { message, style: style ?? 'success' },
  });
}

export function removeFlash(id: string) {
  flashesStore.setState((state) => ({
    flashes: state.flashes.filter((message) => message.id !== id),
  }));
}

/**
 * Remove all regular flashes and remove `keepOnceForNavigation` from navigation flashes
 */
export function tickFlashes() {
  const currentItems = flashesStore.getState().flashes;

  const newItems: FlashStoreItem[] = [];

  for (const item of currentItems) {
    if (item.keepOnceForNavigation)
      newItems.push({ ...item, keepOnceForNavigation: false });
  }

  flashesStore.setState({ flashes: newItems });
}

// Hook

export function useFlashes() {
  const flashes = useFlashesStore((state) => state.flashes);
  return { flashes };
}

// Misc

export function addFlashForNextPageLoad(message: string, style?: FlashStyle) {
  const msg = {
    type: 'flash',
    data: { message, style: style ?? 'success' },
  } satisfies FlashCookie;

  let messages: FlashCookie[] = [];
  const cookieValue = getCookieValue(FLASHES_COOKIE);

  if (cookieValue) {
    messages = JSON.parse(atob(cookieValue)) as FlashCookie[];
  }

  messages.push(msg);
  const newCookieValue = btoa(JSON.stringify(messages));
  createCookie(FLASHES_COOKIE, newCookieValue, 2);
}
